/**
 * Lightweight schema logger for development-only diagnostics
 *
 * Design principles (from LOGGING.md):
 * - Environment-aware: Only logs in development
 * - Zero overhead in production: No logging calls in non-dev environments
 * - Uses debug level for parse failures (appropriate for schema transforms)
 *
 * This utility is intentionally minimal to avoid circular dependencies
 * in the schemas package.
 */

/**
 * Detect if we're in development mode
 * Works in both browser and Node.js environments
 */
function isDevelopment(): boolean {
  // Browser environment
  if (typeof window !== 'undefined') {
    // Check for Vite development mode
    if (typeof (globalThis as any).__VITE_ENV__ !== 'undefined') {
      return (globalThis as any).__VITE_ENV__.MODE === 'development';
    }
    // Check for Next.js development mode
    if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
      return process.env.NODE_ENV === 'development';
    }
    // Default to true in browser for safety (show logs when uncertain)
    return true;
  }

  // Node.js environment
  if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
    return process.env.NODE_ENV === 'development';
  }

  // Default to false (production-safe)
  return false;
}

// Cache the environment check for performance
let _isDev: boolean | null = null;
function getIsDev(): boolean {
  if (_isDev === null) {
    _isDev = isDevelopment();
  }
  return _isDev;
}

/**
 * Log a debug message only in development
 * Used for schema parsing diagnostics
 */
export function schemaDebug(message: string, context?: Record<string, unknown>): void {
  if (!getIsDev()) return;

  if (context) {
    console.debug(`[Schema] ${message}`, context);
  } else {
    console.debug(`[Schema] ${message}`);
  }
}

/**
 * Log a warning only in development
 * Used for recoverable schema parsing issues
 */
export function schemaWarn(message: string, context?: Record<string, unknown>): void {
  if (!getIsDev()) return;

  if (context) {
    console.warn(`[Schema] ${message}`, context);
  } else {
    console.warn(`[Schema] ${message}`);
  }
}














